<script setup>
/**
 * AgentsPortalView
 * Agent dashboard portal with compact week widget,
 * warning banner, and asymmetric quick-actions layout.
 */
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ROUTES } from '../../../core/config/constants.js'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'
import { getTodayAttendance, checkIn, checkOut } from '@/core/services/attendance'
import {
  useHolidays,
  formatDateDisplay,
  formatDateKey,
  DAY_NAMES,
} from '@/core/composables/useHolidays'
import { useBackButtonClose } from '@/core/composables/useBackButtonClose'
import ProfilePhotoPrompt from '@ui/components/agents/ProfilePhotoPrompt.vue'
import AgentsPerformanceWidget from '@ui/components/agents/AgentsPerformanceWidget.vue'
import MyYearWidget from '@ui/components/agents/MyYearWidget.vue'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// ============================================
// Holidays (inline – no separate widget)
// ============================================
const { loading: holidaysLoading, loadHolidays } = useHolidays()

// Week offset: 0 = current week, 1 = next week, etc.
const weekOffset = ref(0)
const todayKey = formatDateKey(new Date())

const offsetWeekDays = computed(() => {
  const today = new Date()
  const dayOfWeek = today.getDay()
  const sunday = new Date(today)
  sunday.setDate(today.getDate() - dayOfWeek + weekOffset.value * 7)

  const days = []
  for (let i = 0; i < 5; i++) {
    const date = new Date(sunday)
    date.setDate(sunday.getDate() + i)
    days.push({
      date,
      dateKey: formatDateKey(date),
      dayName: DAY_NAMES[i],
      isToday: formatDateKey(date) === todayKey,
    })
  }
  return days
})

// Re-read holidays for the offset week
const { holidays } = useHolidays()
const offsetWeekEvents = computed(() => {
  return offsetWeekDays.value.map((day) => ({
    ...day,
    event: holidays.value[day.dateKey] || null,
  }))
})

const hasOffsetWeekEvents = computed(() => {
  return offsetWeekEvents.value.some((day) => day.event)
})

// Next closest event from today onwards
const nextEvent = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const entries = Object.entries(holidays.value)
  let closest = null
  for (const [dateKey, text] of entries) {
    const [y, m, d] = dateKey.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    if (date >= today && (!closest || date < closest.date)) {
      closest = { date, text, dateKey }
    }
  }
  if (!closest) return null
  const diffDays = Math.round((closest.date - today) / 86400000)
  const dayLabel = diffDays === 0 ? 'היום' : diffDays === 1 ? 'מחר' : `בעוד ${diffDays} ימים`
  return { ...closest, dayLabel }
})

function advanceWeek() {
  weekOffset.value++
}

function goBackWeek() {
  if (weekOffset.value > 0) weekOffset.value--
}

// Event detail modal
const eventModal = ref(null) // { dayName, date, event }

function handleDayChipClick(day) {
  if (day.event) {
    eventModal.value = day
  } else {
    router.push({ name: ROUTES.AGENTS_HOLIDAYS })
  }
}

function closeEventModal() {
  eventModal.value = null
}

function navigateToCalendar() {
  closeEventModal()
  router.push({ name: ROUTES.AGENTS_HOLIDAYS })
}

// ============================================
// Perf-strip modal
// ============================================
const perfModal = ref(null)
const isPerfModalOpen = computed(() => perfModal.value !== null)
useBackButtonClose(isPerfModalOpen, closePerfModal)

function handlePerfStripClick() {
  perfModal.value = {}
}

function closePerfModal() {
  perfModal.value = null
}

function navigateToStatistics() {
  closePerfModal()
  router.push({ name: ROUTES.AGENTS_STATISTICS })
}

// ============================================
// Bottom sheet state
// ============================================
const activeSheet = ref(null)

function toggleSheet(name) {
  activeSheet.value = activeSheet.value === name ? null : name
}

function closeSheet() {
  activeSheet.value = null
}

function navigateTo(routeName) {
  closeSheet()
  router.push({ name: routeName })
}

// ============================================
// Attendance
// ============================================
const todayRecord = ref(null)
const loadingAttendance = ref(true)
const actionLoading = ref(false)
const elapsed = ref('00:00:00')
let timerInterval = null

const attendanceStatus = computed(() => {
  if (!todayRecord.value) return 'none'
  if (todayRecord.value.checkOut) return 'done'
  return 'checked-in'
})

function formatTime(timestamp) {
  if (!timestamp) return '—'
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000)
  return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
}

function calcHours(record) {
  if (!record.checkIn || !record.checkOut) return '—'
  const inDate = record.checkIn.toDate
    ? record.checkIn.toDate()
    : new Date(record.checkIn.seconds * 1000)
  const outDate = record.checkOut.toDate
    ? record.checkOut.toDate()
    : new Date(record.checkOut.seconds * 1000)
  const diffMs = outDate - inDate
  const hours = Math.floor(diffMs / 3600000)
  const minutes = Math.floor((diffMs % 3600000) / 60000)
  return `${hours}:${String(minutes).padStart(2, '0')}`
}

function updateElapsed() {
  if (!todayRecord.value?.checkIn) return
  const inDate = todayRecord.value.checkIn.toDate
    ? todayRecord.value.checkIn.toDate()
    : new Date(todayRecord.value.checkIn.seconds * 1000)
  const diff = Date.now() - inDate.getTime()
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  const s = Math.floor((diff % 60000) / 1000)
  elapsed.value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function startTimer() {
  stopTimer()
  updateElapsed()
  timerInterval = setInterval(updateElapsed, 1000)
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

async function loadToday() {
  loadingAttendance.value = true
  try {
    const uid = authStore.userId
    if (!uid) return
    todayRecord.value = await getTodayAttendance(uid)
    if (attendanceStatus.value === 'checked-in') {
      startTimer()
    }
  } catch (err) {
    console.error('Failed to load today attendance:', err)
  } finally {
    loadingAttendance.value = false
  }
}

async function handleCheckIn() {
  if (actionLoading.value) return
  actionLoading.value = true
  try {
    await checkIn({
      agentUid: authStore.userId,
      agentName: authStore.userDisplayName || '',
      agentNumber: String(authStore.userAgentId || ''),
    })
    toast.success('כניסה נרשמה בהצלחה')
    await loadToday()
  } catch (err) {
    console.error('Check-in error:', err)
    toast.error('שגיאה ברישום כניסה')
  } finally {
    actionLoading.value = false
  }
}

async function handleCheckOut() {
  if (actionLoading.value || !todayRecord.value?.id) return
  actionLoading.value = true
  try {
    await checkOut(todayRecord.value.id)
    toast.success('יציאה נרשמה בהצלחה')
    stopTimer()
    await loadToday()
  } catch (err) {
    console.error('Check-out error:', err)
    toast.error('שגיאה ברישום יציאה')
  } finally {
    actionLoading.value = false
  }
}

onMounted(() => {
  loadToday()
  loadHolidays().catch((err) => {
    console.error('Failed to load holidays:', err)
  })
})

onUnmounted(() => {
  stopTimer()
})
</script>

<template>
  <div class="portal-v2">
    <ProfilePhotoPrompt />

    <!-- Content -->
    <div class="portal-content">
      <!-- ========== Week Widget ========== -->
      <section class="week-card">
        <div class="week-header">
          <div class="week-header-label">
            <svg
              class="week-header-icon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span class="week-header-title">השבוע הקרוב</span>
          </div>
          <div class="week-nav-arrows">
            <button
              v-if="weekOffset > 0"
              class="week-nav-btn"
              title="שבוע קודם"
              @click.stop="goBackWeek"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <button class="week-nav-btn" title="שבוע הבא" @click.stop="advanceWeek">
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="holidaysLoading" class="week-loading">
          <div class="spinner"></div>
        </div>

        <!-- Day cards row -->
        <div v-else class="week-days">
          <div
            v-for="day in offsetWeekEvents"
            :key="day.dateKey"
            class="day-chip"
            :class="{ 'day-chip--today': day.isToday, 'day-chip--event': !!day.event }"
            @click="handleDayChipClick(day)"
          >
            <div class="day-chip-top">
              <span class="day-chip-letter">{{ day.dayName }}'</span>
              <span class="day-chip-date">{{ formatDateDisplay(day.date) }}</span>
            </div>
            <span v-if="day.event" class="day-chip-event">{{ day.event }}</span>
          </div>
        </div>

        <!-- Check-in button (overlaid bottom-left of the card) -->
        <div v-if="!loadingAttendance" class="week-checkin-area">
          <!-- Next upcoming event -->
          <div v-if="nextEvent" class="next-event-hint">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span class="next-event-label">{{ nextEvent.dayLabel }}</span>
            <span class="next-event-text">{{ nextEvent.text }}</span>
          </div>

          <!-- Not checked in -->
          <button
            v-if="attendanceStatus === 'none'"
            class="checkin-btn checkin-btn--in"
            :disabled="actionLoading"
            @click.stop="handleCheckIn"
          >
            <div v-if="actionLoading" class="btn-spinner"></div>
            <template v-else>
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>כניסה לנוכחות</span>
            </template>
          </button>

          <!-- Checked in — show elapsed + checkout -->
          <template v-else-if="attendanceStatus === 'checked-in'">
            <div class="checkin-active-info" @click.stop>
              <span class="checkin-pulse"></span>
              <span class="checkin-since">{{ formatTime(todayRecord.checkIn) }}</span>
              <span class="checkin-elapsed">{{ elapsed }}</span>
            </div>
            <button
              class="checkin-btn checkin-btn--out"
              :disabled="actionLoading"
              @click.stop="handleCheckOut"
            >
              <div v-if="actionLoading" class="btn-spinner"></div>
              <template v-else>
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>יציאה מנוכחות</span>
              </template>
            </button>
          </template>

          <!-- Done for the day -->
          <div v-else class="checkin-done-info" @click.stop>
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span
              >{{ formatTime(todayRecord.checkIn) }} ← {{ formatTime(todayRecord.checkOut) }}</span
            >
            <span class="done-hours">{{ calcHours(todayRecord) }} שעות</span>
            <button
              class="checkin-btn checkin-btn--in checkin-btn--small"
              :disabled="actionLoading"
              @click.stop="handleCheckIn"
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>כניסה חוזרת</span>
            </button>
          </div>
        </div>
      </section>

      <!-- ========== Event Detail Modal ========== -->
      <Transition name="event-modal">
        <div v-if="eventModal" class="event-modal-backdrop" @click.self="closeEventModal">
          <div class="event-modal">
            <div class="event-modal-header">
              <span class="event-modal-day"
                >{{ eventModal.dayName }}' {{ formatDateDisplay(eventModal.date) }}</span
              >
              <button class="event-modal-close" @click="closeEventModal">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div class="event-modal-body">
              <svg
                class="event-modal-icon"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span class="event-modal-text">{{ eventModal.event }}</span>
            </div>
            <button class="event-modal-btn" @click="navigateToCalendar">
              <span>לוח שנה מלא</span>
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
        </div>
      </Transition>

      <!-- ========== Perf-Strip Modal ========== -->
      <Transition name="event-modal">
        <div v-if="perfModal" class="event-modal-backdrop" @click.self="closePerfModal">
          <div class="event-modal perf-modal">
            <div class="event-modal-header">
              <span class="event-modal-day">סטטיסטיקות</span>
              <button class="event-modal-close" @click="closePerfModal">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <p class="perf-modal-summary">צפו בסטטיסטיקות המלאות של כל הסוכנים, יעדים וביצועים.</p>

            <div class="perf-modal-actions">
              <button class="perf-modal-btn perf-modal-btn--secondary" @click="closePerfModal">
                סגור
              </button>
              <button class="perf-modal-btn perf-modal-btn--primary" @click="navigateToStatistics">
                <span>עבור לסטטיסטיקות</span>
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- ========== Warning Banner ========== -->
      <div v-if="!loadingAttendance && attendanceStatus === 'none'" class="warning-banner">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <span>לא נרשמה כניסת נוכחות היום!</span>
        <svg
          class="warning-info"
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </div>

      <!-- ========== Performance Strip Row ========== -->
      <div class="perf-strip-row">
        <MyYearWidget class="perf-strip-item" />
        <AgentsPerformanceWidget class="perf-strip-item" @strip-click="handlePerfStripClick" />
      </div>

      <!-- ========== Quick Actions ========== -->
      <section class="quick-actions">
        <h2 class="section-label">הפעלות מהירות</h2>

        <div class="actions-layout">
          <!-- Wallet — large card -->
          <button class="qa-card qa-card--large" @click="navigateTo(ROUTES.AGENTS_WALLET)">
            <div class="qa-large-text">
              <span class="qa-label">קופה</span>
              <span class="qa-subtitle">ניהול קופת סוכן</span>
            </div>
            <div class="qa-icon-circle">
              <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="2" y="6" width="20" height="14" rx="2" ry="2" />
                <path d="M2 10h20" />
                <path d="M16 14h2" />
              </svg>
            </div>
          </button>

          <!-- Right stack: Clients + Route Planning -->
          <div class="qa-stack">
            <!-- Route Planning -->
            <button
              class="qa-card qa-card--compact"
              @click="navigateTo(ROUTES.AGENTS_ROUTE_PLANNING)"
            >
              <div class="qa-compact-content">
                <span class="qa-label">תכנון מסלול</span>
                <span class="qa-subtitle">תכנון מסלול ביקורים יומי</span>
              </div>
              <div class="qa-icon-circle">
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="10" r="3" />
                  <path
                    d="M12 2a8 8 0 00-8 8c0 5.4 7 11.5 7.3 11.8a1 1 0 001.4 0C13 21.5 20 15.4 20 10a8 8 0 00-8-8z"
                  />
                </svg>
              </div>
              <svg
                class="qa-chevron"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <!-- Clients -->
            <button class="qa-card qa-card--compact" @click="navigateTo(ROUTES.AGENTS_CLIENTS)">
              <div class="qa-compact-content">
                <span class="qa-label">לקוחות</span>
                <span class="qa-subtitle">רשימת הלקוחות המשויכים אליך</span>
              </div>
              <div class="qa-icon-circle">
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
              </div>
              <svg
                class="qa-chevron"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Bottom Sheet Backdrop -->
    <Transition name="fade">
      <div v-if="activeSheet" class="sheet-backdrop" @click="closeSheet" />
    </Transition>

    <!-- Sheet Panels -->
    <Transition name="slide-up">
      <div v-if="activeSheet === 'new'" class="sheet-panel">
        <div class="sheet-handle" />
        <button class="sheet-item" @click="navigateTo(ROUTES.AGENTS_CREATE_CLIENT)">
          <div class="sheet-item-icon">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="20" y1="8" x2="20" y2="14" />
              <line x1="23" y1="11" x2="17" y2="11" />
            </svg>
          </div>
          <span class="sheet-item-label">יצירת לקוח חדש</span>
        </button>
      </div>
    </Transition>

    <Transition name="slide-up">
      <div v-if="activeSheet === 'info'" class="sheet-panel">
        <div class="sheet-handle" />
        <button class="sheet-item" @click="navigateTo(ROUTES.AGENTS_GOALS)">
          <div class="sheet-item-icon">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="6" />
              <circle cx="12" cy="12" r="2" />
            </svg>
          </div>
          <span class="sheet-item-label">יעדים</span>
        </button>
        <button class="sheet-item" @click="navigateTo(ROUTES.AGENTS_HOLIDAYS)">
          <div class="sheet-item-icon">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <span class="sheet-item-label">חגים</span>
        </button>
        <button class="sheet-item" @click="navigateTo(ROUTES.AGENTS_STATISTICS)">
          <div class="sheet-item-icon">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
          </div>
          <span class="sheet-item-label">סטטיסטיקות</span>
        </button>
        <button class="sheet-item" @click="navigateTo(ROUTES.AGENTS_ATTENDANCE)">
          <div class="sheet-item-icon">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <span class="sheet-item-label">נוכחות</span>
        </button>
      </div>
    </Transition>

    <Transition name="slide-up">
      <div v-if="activeSheet === 'requests'" class="sheet-panel">
        <div class="sheet-handle" />
        <button class="sheet-item" @click="navigateTo(ROUTES.AGENTS_REQUESTS)">
          <div class="sheet-item-icon">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M17 3a2.85 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
            </svg>
          </div>
          <span class="sheet-item-label">בקשות חופשה</span>
        </button>
      </div>
    </Transition>

    <!-- Bottom Bar -->
    <div class="bottom-bar" :class="{ 'bottom-bar--sheet-open': activeSheet }">
      <button
        class="bar-btn"
        :class="{ 'bar-btn--active': activeSheet === 'new' }"
        @click="toggleSheet('new')"
      >
        חדש
      </button>
      <button
        class="bar-btn"
        :class="{ 'bar-btn--active': activeSheet === 'info' }"
        @click="toggleSheet('info')"
      >
        מידע לסוכן
      </button>
      <button
        class="bar-btn"
        :class="{ 'bar-btn--active': activeSheet === 'requests' }"
        @click="toggleSheet('requests')"
      >
        בקשות
      </button>
    </div>
  </div>
</template>

<style scoped>
/* ============================================
   Root container
   ============================================ */
.portal-v2 {
  height: calc(100vh - var(--header-height));
  display: flex;
  flex-direction: column;
  max-width: 940px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
}

/* ============================================
   Content layer
   ============================================ */
.portal-content {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-lg);
  padding-bottom: 80px;
}

/* ============================================
   Week Card
   ============================================ */
.week-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
}

.week-card > * {
  position: relative;
  z-index: 1;
}

.week-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  direction: rtl;
}

.week-header-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  background: rgba(255, 255, 255, 0.6);
  padding: 4px 12px;
  border-radius: var(--border-radius);
}

.week-header-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.week-header-icon {
  color: var(--color-primary);
}

.week-nav-arrows {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-right: auto;
}

.week-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: var(--bg-tertiary);
  color: var(--color-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 0;
}

.week-nav-btn:hover {
  background: var(--color-primary);
  color: #fff;
}

/* Loading */
.week-loading {
  display: flex;
  justify-content: center;
  padding: var(--spacing-md) 0;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Day chips row */
.week-days {
  display: flex;
  gap: var(--spacing-sm);
  direction: rtl;
  align-items: flex-start;
  transition: all 0.3s ease;
}

.day-chip {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: var(--spacing-sm) var(--spacing-xs);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  border: 1.5px solid transparent;
  transition: all 0.3s ease;
  min-width: 0;
  cursor: pointer;
}

.day-chip:hover {
  border-color: var(--color-primary);
}

.day-chip-top {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.day-chip--today {
  background: #dcfce7;
  border-color: var(--color-success);
}

.day-chip--today .day-chip-letter {
  color: var(--color-success);
  font-weight: var(--font-weight-bold);
}

.day-chip--event {
  background: var(--bg-secondary);
  border-color: var(--color-success);
}

.day-chip-letter,
.day-chip-date {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

/* Event text inside day chip */
.day-chip-event {
  font-size: 10px;
  color: var(--color-success);
  font-weight: var(--font-weight-medium);
  background: rgba(16, 185, 129, 0.12);
  padding: 1px 6px;
  border-radius: var(--border-radius-full);
  line-height: 1.3;
  word-break: break-word;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  animation: chip-event-in 0.3s ease;
}

@keyframes chip-event-in {
  from {
    opacity: 0;
    max-height: 0;
    padding: 0 6px;
    margin: 0;
  }
  to {
    opacity: 1;
    max-height: 30px;
    padding: 1px 6px;
  }
}

/* ============================================
   Check-in area (bottom of week card)
   ============================================ */
.week-checkin-area {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.checkin-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  border-radius: 5px;
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.checkin-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkin-btn--in {
  background: #16a34a;
}

.checkin-btn--in:not(:disabled):hover {
  background: #15803d;
}

.checkin-btn--out {
  background: var(--color-warning);
}

.checkin-btn--out:not(:disabled):hover {
  background: #d97706;
}

.checkin-btn--small {
  font-size: var(--font-size-xs);
  padding: 6px 14px;
  margin-right: auto;
}

.checkin-active-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 1;
}

.checkin-pulse {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-success);
  flex-shrink: 0;
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.3);
  }
}

.checkin-since {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.checkin-elapsed {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
  font-family: 'Rubik', monospace;
}

.checkin-done-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 1;
  color: var(--color-success);
  font-size: var(--font-size-sm);
}

.done-hours {
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.next-event-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-inline-start: auto;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
}

.next-event-hint svg {
  flex-shrink: 0;
  color: var(--color-info);
}

.next-event-label {
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.next-event-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 500px) {
  .next-event-hint {
    display: none;
  }
}

.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ============================================
   Event Detail Modal
   ============================================ */
.event-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
}

.event-modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: var(--spacing-lg);
  min-width: 280px;
  max-width: 360px;
  width: 100%;
}

.event-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-md);
  direction: rtl;
}

.event-modal-day {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.event-modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: var(--bg-tertiary);
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  transition: all var(--transition-fast);
}

.event-modal-close:hover {
  background: var(--border-color);
  color: var(--text-primary);
}

.event-modal-body {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: rgba(16, 185, 129, 0.06);
  border: 1px solid rgba(16, 185, 129, 0.15);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-md);
  direction: rtl;
}

.event-modal-icon {
  color: var(--color-success);
  flex-shrink: 0;
  margin-top: 1px;
}

.event-modal-text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  line-height: 1.5;
  word-break: break-word;
}

.event-modal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  width: 100%;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  color: var(--color-primary);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.event-modal-btn:hover {
  background: var(--color-primary);
  color: #fff;
  border-color: var(--color-primary);
}

/* Event modal transition */
.event-modal-enter-active,
.event-modal-leave-active {
  transition: opacity 0.2s ease;
}

.event-modal-enter-active .event-modal,
.event-modal-leave-active .event-modal {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.event-modal-enter-from,
.event-modal-leave-to {
  opacity: 0;
}

.event-modal-enter-from .event-modal {
  transform: scale(0.95);
  opacity: 0;
}

.event-modal-leave-to .event-modal {
  transform: scale(0.95);
  opacity: 0;
}

/* ============================================
   Perf-Strip Modal
   ============================================ */
.perf-modal-summary {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-align: center;
  direction: rtl;
  margin: 0 0 var(--spacing-md);
  line-height: 1.5;
}

.perf-modal-actions {
  display: flex;
  gap: var(--spacing-sm);
  direction: rtl;
}

.perf-modal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  flex: 1;
  padding: 10px;
  border-radius: var(--border-radius);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-fast);
  border: 1px solid var(--border-color);
}

.perf-modal-btn--secondary {
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.perf-modal-btn--secondary:hover {
  background: var(--border-color);
  color: var(--text-primary);
}

.perf-modal-btn--primary {
  background: var(--color-primary);
  color: #fff;
  border-color: var(--color-primary);
}

.perf-modal-btn--primary:hover {
  background: var(--color-primary-hover);
  border-color: var(--color-primary-hover);
}

/* ============================================
   Warning Banner
   ============================================ */
.warning-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 5px;
  margin-bottom: var(--spacing-lg);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.warning-info {
  opacity: 0.5;
}

/* ============================================
   Agents Performance Strip
   ============================================ */

.perf-strip-row {
  display: flex;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

.perf-strip-item {
  width: 50%;
  min-width: 0;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
}

@media (max-width: 768px) {
  .perf-strip-item {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .perf-strip-row {
    flex-direction: column;
  }
}

/* ============================================
   Quick Actions
   ============================================ */
.quick-actions {
  margin-bottom: var(--spacing-lg);
}

.section-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md);
  text-align: right;
}

.actions-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
  direction: rtl;
}

/* Large card (Route Planning) */
.qa-card {
  background: var(--bg-primary);
  border: none;
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  font-family: var(--font-family);
  text-align: right;
  transition: all var(--transition-fast);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.qa-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.qa-card:active {
  transform: translateY(0);
}

.qa-card--large {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='120' fill='none'%3E%3C!-- Bills --%3E%3Crect x='180' y='15' width='60' height='35' rx='4' fill='%2394a3b8' opacity='0.05' transform='rotate(-8 210 32)'/%3E%3Crect x='195' y='25' width='60' height='35' rx='4' fill='%2394a3b8' opacity='0.04' transform='rotate(5 225 42)'/%3E%3C!-- Coins --%3E%3Ccircle cx='250' cy='85' r='18' fill='%2394a3b8' opacity='0.05'/%3E%3Ccircle cx='250' cy='85' r='12' fill='none' stroke='%2394a3b8' stroke-width='1' opacity='0.06'/%3E%3Ccircle cx='230' cy='75' r='14' fill='%2394a3b8' opacity='0.04'/%3E%3Ccircle cx='230' cy='75' r='9' fill='none' stroke='%2394a3b8' stroke-width='0.8' opacity='0.05'/%3E%3C!-- Currency symbol --%3E%3Ctext x='247' y='91' font-family='sans-serif' font-size='14' fill='%2394a3b8' opacity='0.08' font-weight='bold'%3E₪%3C/text%3E%3C!-- Card outline --%3E%3Crect x='160' y='65' width='45' height='28' rx='4' fill='none' stroke='%2394a3b8' stroke-width='1' opacity='0.06'/%3E%3Cline x1='160' y1='75' x2='205' y2='75' stroke='%2394a3b8' stroke-width='1' opacity='0.05'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: left bottom;
  background-size: auto 100%;
}

.qa-large-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qa-large-text .qa-label {
  font-size: var(--font-size-2xl);
}

.qa-icon-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: var(--bg-tertiary);
  border-radius: 50%;
  color: var(--color-primary);
  flex-shrink: 0;
  margin-bottom: var(--spacing-xs);
}

.qa-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  display: block;
}

.qa-subtitle {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  display: block;
}

/* Compact stack */
.qa-stack {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.qa-card--compact {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) var(--spacing-lg);
  flex: 1;
}

.qa-compact-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qa-chevron {
  color: var(--text-muted);
  flex-shrink: 0;
  transition: transform var(--transition-fast);
}

.qa-card--compact:hover .qa-chevron {
  transform: translateX(-3px);
}

/* ============================================
   Bottom Bar (unchanged from V1)
   ============================================ */
.bottom-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px var(--spacing-md);
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  z-index: 30;
  position: relative;
}

.bar-btn {
  flex: 0 0 auto;
  padding: 8px 20px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.bottom-bar--sheet-open {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.bar-btn:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
  background: var(--bg-secondary);
}

.bar-btn--active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.bar-btn--active:hover {
  background: var(--color-primary-hover);
  border-color: var(--color-primary-hover);
  color: #fff;
}

/* ============================================
   Bottom Sheet Backdrop & Panel (same as V1)
   ============================================ */
.sheet-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 15;
}

.sheet-panel {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: var(--bg-primary);
  border-radius: 16px 16px 0 0;
  padding: 8px 16px 16px;
  padding-bottom: 72px;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);
}

.sheet-handle {
  width: 36px;
  height: 4px;
  background: var(--color-gray-300);
  border-radius: 2px;
  margin: 0 auto 12px;
}

.sheet-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  padding: 14px 12px;
  border: none;
  border-radius: var(--border-radius);
  background: transparent;
  cursor: pointer;
  text-align: right;
  font-family: var(--font-family);
  transition: background var(--transition-fast);
}

.sheet-item:hover {
  background: var(--bg-secondary);
}

.sheet-item:active {
  background: var(--bg-tertiary);
}

.sheet-item + .sheet-item {
  border-top: 1px solid var(--border-color);
}

.sheet-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--bg-tertiary);
  border-radius: var(--border-radius);
  color: var(--color-primary);
  flex-shrink: 0;
}

.sheet-item-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

/* ============================================
   Transitions
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.25s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 768px) {
  .portal-content {
    padding: var(--spacing-md);
    padding-bottom: 80px;
  }

  .week-card {
    padding: var(--spacing-md);
  }

  .bottom-bar {
    justify-content: space-between;
    gap: 0;
  }

  .bar-btn {
    flex: 1;
    padding: 10px 8px;
    margin: 0 4px;
  }
}

@media (max-width: 600px) {
  .actions-layout {
    grid-template-columns: 1fr;
  }

  .day-chip {
    padding: var(--spacing-xs);
  }

  .day-chip-letter,
  .day-chip-date {
    font-size: var(--font-size-xs);
  }
}

@media (max-width: 480px) {
  .week-days {
    gap: 4px;
  }

  .day-chip {
    padding: 4px 2px;
  }
}

@media (min-width: 940px) {
  .sheet-backdrop {
    background: transparent;
    pointer-events: none;
  }

  .sheet-panel {
    box-shadow: none;
  }
}

@media (min-width: 941px) {
  .bottom-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
}

/* ============================================
   Desktop compact overrides
   ============================================ */
@media (min-width: 941px) {
  .portal-v2 {
    max-width: 680px;
  }

  .portal-content {
    padding: var(--spacing-md) var(--spacing-lg);
    padding-bottom: 70px;
  }

  .portal-title {
    font-size: var(--font-size-xl);
    margin-bottom: var(--spacing-md);
  }

  /* Week card compact */
  .week-card {
    padding: var(--spacing-md);
    margin-bottom: var(--spacing-sm);
  }

  .week-header {
    margin-bottom: var(--spacing-sm);
  }

  .week-header-title {
    font-size: var(--font-size-sm);
  }

  .week-header-icon {
    width: 18px;
    height: 18px;
  }

  .day-chip {
    padding: 6px 4px;
    gap: 2px;
  }

  .day-chip-letter {
    font-size: var(--font-size-xs);
  }

  .day-chip-date {
    font-size: 10px;
  }

  .week-checkin-area {
    margin-top: var(--spacing-sm);
    padding-top: var(--spacing-sm);
  }

  .checkin-btn {
    padding: 8px 18px;
    font-size: var(--font-size-xs);
  }

  /* Warning banner compact */
  .warning-banner {
    padding: 6px var(--spacing-md);
    margin-bottom: var(--spacing-md);
    font-size: var(--font-size-xs);
  }

  /* Quick actions compact */
  .quick-actions {
    margin-bottom: var(--spacing-md);
  }

  .section-label {
    font-size: var(--font-size-sm);
    margin-bottom: var(--spacing-sm);
  }

  .actions-layout {
    gap: var(--spacing-sm);
  }

  .qa-icon-circle {
    width: 36px;
    height: 36px;
    margin-bottom: 2px;
  }

  .qa-icon-circle svg {
    width: 18px;
    height: 18px;
  }

  .qa-label {
    font-size: var(--font-size-sm);
  }

  .qa-subtitle {
    font-size: 10px;
  }

  /* Compact cards tighter */
  .qa-stack {
    gap: var(--spacing-sm);
  }

  .qa-card--compact {
    padding: var(--spacing-sm) var(--spacing-md);
    gap: var(--spacing-sm);
  }

  .qa-card--compact .qa-icon-circle {
    width: 36px;
    height: 36px;
    margin-bottom: 0;
  }

  /* Bottom bar compact */
  .bottom-bar {
    padding: 10px var(--spacing-md);
  }

  .bar-btn {
    padding: 8px 20px;
    font-size: var(--font-size-sm);
  }
}
</style>
